import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Archive, Trash2 } from "lucide-react";
import { fmtBRL } from "@/lib/api";

export function ArquivarDialog({ open, onOpenChange, contrato, motivo, setMotivo, motivoCustom, setMotivoCustom, onConfirmar }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="modal-arquivar-contrato">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive size={18} /> Arquivar contrato
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed">
            Arquivar o contrato de <b className="break-anywhere">{contrato?.razao_social}</b>?
          </p>
          <div className="border border-info/40 bg-info/5 p-3 rounded-sm text-xs space-y-1">
            <div className="font-semibold text-info uppercase tracking-widest label-tag">O que acontece:</div>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
              <li><b>{contrato?.unidades?.length || 0}</b> unidade(s) voltam para <b>LIVRE</b></li>
              <li>Histórico de ocupação é gravado em cada unidade</li>
              <li>Contrato preservado — pode ser <b>restaurado</b> depois</li>
              <li>Boletos e reajustes anteriores permanecem intactos</li>
            </ul>
          </div>
          <div>
            <Label className="label-tag">Motivo do encerramento</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="arquivar-motivo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Fim de Contrato">Fim de Contrato</SelectItem>
                <SelectItem value="Rescisão">Rescisão</SelectItem>
                <SelectItem value="Troca de Ponto">Troca de Ponto</SelectItem>
                <SelectItem value="Inadimplência">Inadimplência</SelectItem>
                <SelectItem value="Distrato Amigável">Distrato Amigável</SelectItem>
                <SelectItem value="Outro">Outro (descrever)</SelectItem>
              </SelectContent>
            </Select>
            {motivo === "Outro" && (
              <Input data-testid="arquivar-motivo-custom" className="mt-2" value={motivoCustom}
                onChange={(e) => setMotivoCustom(e.target.value)} placeholder="Descreva o motivo" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="arquivar-cancelar">Cancelar</Button>
          <Button data-testid="arquivar-confirmar" onClick={onConfirmar}
            className="bg-foreground text-background hover:bg-foreground/90">
            <Archive size={14} className="mr-2" /> Arquivar contrato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExcluirDialog({ open, onOpenChange, contrato, onConfirmar }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="modal-excluir-contrato">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <Trash2 size={18} /> Excluir contrato
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed">
            Tem certeza que deseja excluir o contrato de <b className="break-anywhere">{contrato?.razao_social}</b>?
          </p>
          <div className="border border-warning/40 bg-warning/5 p-3 rounded-sm text-xs space-y-1">
            <div className="font-semibold text-warning uppercase tracking-widest label-tag">O que vai acontecer:</div>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
              <li><b>{contrato?.unidades?.length || 0}</b> unidade(s) vinculada(s) voltam para <b>LIVRE</b></li>
              <li>Boletos <b>em aberto</b> deste contrato serão removidos</li>
              <li>Boletos <b>já pagos</b> serão preservados (histórico)</li>
              <li>Tokens de acesso do lojista são invalidados</li>
            </ul>
          </div>
          <p className="text-xs text-destructive font-semibold">Esta ação é permanente e não pode ser desfeita.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="excluir-cancelar">Cancelar</Button>
          <Button data-testid="excluir-confirmar" onClick={onConfirmar}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            <Trash2 size={14} className="mr-2" /> Excluir permanentemente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReajusteDialog({ open, onOpenChange, contrato, percentual, setPercentual, onAplicar }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Reajuste — {contrato?.razao_social}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-tag">Percentual (%)</Label>
            <Input data-testid="reajuste-percent" type="number" step="0.01" value={percentual}
              onChange={(e) => setPercentual(e.target.value)} placeholder="Ex: 4.5" />
            <div className="text-xs text-muted-foreground mt-2">
              Índice: {contrato?.indice_reajuste} · Atual: {fmtBRL(contrato?.valor_aluguel)}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="reajuste-aplicar" onClick={onAplicar}
            className="bg-foreground text-background hover:bg-foreground/90">Aplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
